// Penielly, SiliconBlast's photo library. Rhema searches it, sends photos from it into chats as
// ordinary encrypted media, and saves photos people received into it. Photo bytes travel between
// the device and Penielly's storage directly. The Rhema server only passes the small JSON calls on.

import type { SendMediaOptions } from "../client";
import type { FileMeta } from "../media";
import { LinkError, type LinksApi } from "./api";

// Shapes from penielly/backend app/schemas. Keep in step with Penielly.

export type PeniellyItem = {
  id: string;
  kind: "image" | "video";
  mime: string;
  width: number | null;
  height: number | null;
  captured_at: string;
  captured_at_estimated?: boolean;
  status?: string;
  favorite?: boolean;
  place: string | null;
  /** Signed thumbnail URL, about 24 hours. Null until Penielly has made it. */
  thumb_url: string | null;
  screen_url: string | null;
  proxy_url: string | null;
};

export type PeniellySearchParams = {
  q?: string;
  person_id?: string;
  place?: string;
  /** YYYY-MM-DD */
  date_from?: string;
  date_to?: string;
  type?: "image" | "video";
  favorite?: boolean;
  limit?: number;
};

export type PeniellySearchResults = { query: string; items: PeniellyItem[] };

export type PeniellyTimelinePage = { items: PeniellyItem[]; next_cursor: string | null };

export type PeniellyAlbum = { id: string; title: string; count: number; cover_url: string | null };

export type PeniellyAlbumDetail = { id: string; title: string; items: PeniellyItem[] };

export type PeniellyShared = { title: string; cover_url: string | null; count: number };

export type PeniellyUploadSession = {
  status: "ready" | "already_uploaded";
  media_id: string;
  content_hash?: string;
  /** Signed URL that opens a resumable upload, valid for 60 minutes. Null when already uploaded. */
  upload_url: string | null;
};

/** Upload chunk size. Storage wants multiples of 256 KiB for every chunk but the last. */
export const UPLOAD_CHUNK_BYTES = 8 * 1024 * 1024;

/** Files Penielly accepts. Anything else is refused before it leaves the device. */
export const PENIELLY_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/gif",
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/x-matroska",
];

/** How many photos save to Penielly at the same time. */
export const SAVE_CONCURRENCY = 3;

const SHARED_LINK = /https:\/\/(?:www\.)?penielly\.com\/shared\/([A-Za-z0-9_-]{16,64})/;

/** The token in a Penielly shared-album link, or null. */
export function sharedAlbumToken(text: string): string | null {
  return SHARED_LINK.exec(text)?.[1] ?? null;
}

export function sharedAlbumUrl(token: string): string {
  return `https://penielly.com/shared/${token}`;
}

export function canSaveToPenielly(mime: string): boolean {
  return PENIELLY_TYPES.includes(mime.split(";")[0]?.trim().toLowerCase() ?? "");
}

async function sha256Hex(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  return Array.from(digest, (b) => b.toString(16).padStart(2, "0")).join("");
}

function extension(mime: string): string {
  const sub = mime.split("/")[1] ?? "bin";
  return { jpeg: "jpg", quicktime: "mov", "x-matroska": "mkv" }[sub] ?? sub;
}

/** Runs jobs with at most `limit` at once and keeps the results in order. */
export async function inBatches<T, R>(
  items: T[],
  limit: number,
  job: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      try {
        results[i] = { status: "fulfilled", value: await job(items[i] as T, i) };
      } catch (reason) {
        results[i] = { status: "rejected", reason };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

/** What a photo needs to be saved to Penielly. */
export type SaveInput = {
  blob: Blob;
  mime: string;
  name?: string;
  width?: number;
  height?: number;
  /** When the photo was taken or sent. Penielly needs a date. */
  capturedAt?: string;
};

export type PeniellyOptions = {
  /** Fetches from Penielly's storage. The browser's fetch, without cookies. */
  fetch?: typeof fetch;
  /** Bytes per upload chunk. Tests make it small. */
  chunkBytes?: number;
};

/** Something that sends a file into a Rhema chat as encrypted media. RhemaClient does. */
export type MediaSender = {
  sendMedia(chatId: string, file: Blob, opts: SendMediaOptions): Promise<void>;
};

export class PeniellyLink {
  private readonly doFetch: typeof fetch;
  private readonly chunkBytes: number;

  constructor(
    private readonly links: LinksApi,
    opts: PeniellyOptions = {},
  ) {
    this.doFetch = opts.fetch ?? ((...args) => fetch(...args));
    this.chunkBytes = opts.chunkBytes ?? UPLOAD_CHUNK_BYTES;
  }

  search(params: PeniellySearchParams): Promise<PeniellySearchResults> {
    return this.links.gatewayJson("penielly", "GET", "/v1/search", {
      query: { ...params, q: params.q ?? "", favorite: params.favorite ? "true" : undefined },
    });
  }

  timeline(opts: { before?: string; limit?: number } = {}): Promise<PeniellyTimelinePage> {
    return this.links.gatewayJson("penielly", "GET", "/v1/media/timeline", { query: opts });
  }

  albums(): Promise<PeniellyAlbum[]> {
    return this.links.gatewayJson("penielly", "GET", "/v1/albums");
  }

  album(id: string): Promise<PeniellyAlbumDetail> {
    return this.links.gatewayJson("penielly", "GET", `/v1/albums/${encodeURIComponent(id)}`);
  }

  /** Title, cover and count for a shared album link. */
  async shared(token: string): Promise<PeniellyShared> {
    const data = await this.links.gatewayJson<
      Partial<PeniellyShared> & { title?: string; items?: PeniellyItem[] }
    >("penielly", "GET", `/v1/shared/${encodeURIComponent(token)}`);
    // Penielly may answer with the full item list. Work out the cover and count from it.
    return {
      title: data.title || "Shared album",
      cover_url: data.cover_url ?? data.items?.find((i) => i.thumb_url)?.thumb_url ?? null,
      count: data.count ?? data.items?.length ?? 0,
    };
  }

  /** A signed URL for the original, valid for 30 minutes. */
  downloadUrl(id: string): Promise<{ url: string }> {
    return this.links.gatewayJson("penielly", "GET", `/v1/media/${encodeURIComponent(id)}/download-url`);
  }

  /** Downloads the original straight from Penielly's storage. */
  async fetchOriginal(item: Pick<PeniellyItem, "id" | "mime" | "captured_at">): Promise<File> {
    const { url } = await this.downloadUrl(item.id);
    const res = await this.doFetch(url, { credentials: "omit" });
    if (!res.ok) throw new LinkError("failed", res.status, "penielly", "That photo could not be downloaded.");
    const blob = await res.blob();
    const mime = blob.type || item.mime;
    const day = item.captured_at.slice(0, 10);
    return new File([blob], `Penielly ${day}.${extension(mime)}`, { type: mime });
  }

  /**
   * Sends a Penielly photo into a chat. The original comes to the device and then goes out like any
   * other photo, encrypted with its own key. `describe` adds the thumbnail and dimensions.
   */
  async sendToChat(
    sender: MediaSender,
    chatId: string,
    item: PeniellyItem,
    describe?: (file: File, kind: "photo" | "video") => Promise<FileMeta>,
  ): Promise<void> {
    const file = await this.fetchOriginal(item);
    const kind = item.kind === "video" ? "video" : "photo";
    const meta: FileMeta = describe
      ? await describe(file, kind)
      : {
          mime: file.type,
          name: file.name,
          ...(item.width ? { width: item.width } : {}),
          ...(item.height ? { height: item.height } : {}),
        };
    await sender.sendMedia(chatId, file, { kind, meta });
  }

  /**
   * Saves a photo to Penielly. The device asks for an upload session, then sends the bytes straight
   * to storage. Penielly notices the new file itself, so there is nothing to finish.
   */
  async save(input: SaveInput): Promise<{ media_id: string; duplicate: boolean }> {
    const mime = input.mime.split(";")[0]?.trim().toLowerCase() ?? "";
    if (!canSaveToPenielly(mime))
      throw new LinkError("invalid", 415, "penielly", "Penielly does not take this kind of file.");
    const bytes = new Uint8Array(await input.blob.arrayBuffer());
    const session = await this.links.gatewayJson<PeniellyUploadSession>(
      "penielly",
      "POST",
      "/v1/uploads/session",
      {
        json: {
          content_hash: await sha256Hex(bytes),
          mime,
          size_bytes: bytes.byteLength,
          captured_at: input.capturedAt ?? new Date().toISOString(),
          ...(input.name ? { original_filename: input.name.slice(0, 512) } : {}),
          ...(input.width ? { width: input.width } : {}),
          ...(input.height ? { height: input.height } : {}),
        },
      },
    );
    if (session.status === "already_uploaded" || !session.upload_url)
      return { media_id: session.media_id, duplicate: true };
    await this.putResumable(session.upload_url, bytes, mime);
    return { media_id: session.media_id, duplicate: false };
  }

  /** Saves several photos, three at a time. Each result says whether that photo was saved. */
  saveMany(inputs: (SaveInput | (() => Promise<SaveInput>))[]) {
    return inBatches(inputs, SAVE_CONCURRENCY, async (input) =>
      this.save(typeof input === "function" ? await input() : input),
    );
  }

  /**
   * A Google Cloud Storage resumable upload. An empty POST to the signed URL with
   * `x-goog-resumable: start` opens a session, and its Location header is the session URI. The
   * bytes then go to that URI in PUT chunks, each with a Content-Range header. Storage answers
   * 308 until the last chunk.
   */
  private async putResumable(url: string, bytes: Uint8Array<ArrayBuffer>, mime: string): Promise<void> {
    const start = await this.doFetch(url, {
      method: "POST",
      headers: { "x-goog-resumable": "start", "Content-Type": mime },
      credentials: "omit",
    });
    const session = start.headers.get("Location");
    if (!start.ok || !session)
      throw new LinkError("failed", start.status, "penielly", "Penielly did not accept the upload.");

    const size = bytes.byteLength;
    let offset = 0;
    let stalls = 0;
    while (offset < size) {
      const end = Math.min(offset + this.chunkBytes, size);
      const res = await this.doFetch(session, {
        method: "PUT",
        headers: { "Content-Range": `bytes ${offset}-${end - 1}/${size}` },
        body: bytes.subarray(offset, end),
        credentials: "omit",
      });
      if (res.ok) return;
      if (res.status !== 308)
        throw new LinkError("failed", res.status, "penielly", "The upload to Penielly failed.");
      // Range says how much storage kept, as "bytes=0-N". Without it, assume the whole chunk.
      const kept = /bytes=0-(\d+)/.exec(res.headers.get("Range") ?? "");
      const next = kept ? Number(kept[1]) + 1 : end;
      stalls = next > offset ? 0 : stalls + 1;
      if (stalls >= 3) throw new LinkError("failed", 0, "penielly", "The upload to Penielly stopped.");
      offset = next;
    }
    // Every byte was acknowledged with 308 but the last answer never came. Ask storage once more.
    const res = await this.doFetch(session, {
      method: "PUT",
      headers: { "Content-Range": `bytes */${size}` },
      credentials: "omit",
    });
    if (!res.ok) throw new LinkError("failed", res.status, "penielly", "The upload to Penielly failed.");
  }
}
